import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Input } from '../ui/input';
import { Progress } from '../ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Users, CheckCircle, Clock, AlertCircle, Search, Loader2, Eye, Mail, Target, TrendingUp, Calendar, Phone, Building2, Briefcase, ChevronLeft, ChevronRight, Award, Trash2 } from 'lucide-react';
import { performanceApi, employeeApi, leaveApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import EmployeeDetailView from './EmployeeDetailView';
import { cn } from '../../lib/utils';

interface ManagerTeamViewProps {
  year: number;
  managerId: string;
}

const ManagerTeamView: React.FC<ManagerTeamViewProps> = ({ year, managerId }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<any>(null);
  const [viewGoals, setViewGoals] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const [actionItemsPage, setActionItemsPage] = useState(1);
  const itemsPerPage = 10;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch team overview
  const { data: teamOverview = [], isLoading } = useQuery<any[]>({
    queryKey: ['teamOverview', managerId, year],
    queryFn: () => performanceApi.getTeamOverview({ manager_id: managerId, year }),
  });

  const { data: employees = [] } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeeApi.getAll({}),
  });

  // Fetch leave policies and requests for all team members
  const { data: policies = [] } = useQuery<any[]>({
    queryKey: ['leavePolicies'],
    queryFn: () => leaveApi.policies.getAll(),
  });

  const { data: allLeaveRequests = [] } = useQuery<any[]>({
    queryKey: ['allLeaveRequests'],
    queryFn: () => leaveApi.requests.getAll({}),
  });

  // Fetch goals for selected employee
  const { data: employeeGoals = [], refetch: refetchGoals } = useQuery<any[]>({
    queryKey: ['employeeGoals', selectedEmployee?.employee_id, year],
    queryFn: () => performanceApi.goals.getAll({ employee_id: selectedEmployee?.employee_id, year }),
    enabled: !!selectedEmployee?.employee_id && viewGoals,
  });

  // Approve goal mutation
  const approveGoalMutation = useMutation({
    mutationFn: ({ goalId, comments }: { goalId: string; comments?: string }) =>
      performanceApi.approveGoal(goalId, { manager_comments: comments }),
    onSuccess: () => {
      toast({
        title: 'Goal Approved',
        description: 'The goal has been successfully approved.',
      });
      refetchGoals();
      queryClient.invalidateQueries({ queryKey: ['teamOverview', managerId, year] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to approve goal. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Reject goal mutation
  const rejectGoalMutation = useMutation({
    mutationFn: ({ goalId, comments }: { goalId: string; comments?: string }) =>
      performanceApi.rejectGoal(goalId, { manager_comments: comments }),
    onSuccess: () => {
      toast({
        title: 'Goal Rejected',
        description: 'The goal has been rejected with your feedback.',
      });
      refetchGoals();
      queryClient.invalidateQueries({ queryKey: ['teamOverview', managerId, year] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to reject goal. Please try again.',
        variant: 'destructive',
      });
    },
  });

  // Calculate leave balance for an employee
  const calculateLeaveBalance = (employeeId: string) => {
    if (!policies || policies.length === 0) {
      return { total: 0, used: 0, available: 0, pending: 0 };
    }

    let totalEntitled = 0;
    let totalUsed = 0;
    let totalAvailable = 0;
    let totalPending = 0;

    policies.forEach((policy: any) => {
      const approvedLeaves = allLeaveRequests.filter(
        (req: any) => req.employee_id === employeeId && req.leave_policy_id === policy.id && req.status === 'APPROVED'
      ).reduce((sum: number, req: any) => sum + (req.days_count || 1), 0);

      const pendingLeaves = allLeaveRequests.filter(
        (req: any) => req.employee_id === employeeId && req.leave_policy_id === policy.id && req.status === 'PENDING'
      ).reduce((sum: number, req: any) => sum + (req.days_count || 1), 0);

      const policyTotal = policy.days_per_year || 0;
      const available = Math.max(0, policyTotal - approvedLeaves - pendingLeaves);

      totalEntitled += policyTotal;
      totalUsed += approvedLeaves;
      totalPending += pendingLeaves;
      totalAvailable += available;
    });

    return {
      total: totalEntitled,
      used: totalUsed,
      available: totalAvailable,
      pending: totalPending,
    };
  };

  // Filter team members by search
  const filteredTeam = teamOverview.filter((member: any) =>
    member.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'GOAL_SETTING':
        return 'bg-blue-50 text-blue-700 border-blue-200/50 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-900/50';
      case 'KPI_DEFINITION':
        return 'bg-purple-50 text-purple-700 border-purple-200/50 dark:bg-purple-900/20 dark:text-purple-400 dark:border-purple-900/50';
      case 'MANAGER_REVIEW':
        return 'bg-amber-50 text-amber-700 border-amber-200/50 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-900/50';
      case 'ONE_ON_ONE':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200/50 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-900/50';
      case 'COMPLETED':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/50';
      default:
        return 'bg-slate-50 text-slate-700 border-slate-200/50 dark:bg-slate-900/20 dark:text-slate-400 dark:border-slate-900/50';
    }
  };

  const getStageBadge = (stage: string) => {
    const label = stage.replace(/_/g, ' ');
    return (
      <Badge className={cn("px-2.5 py-1 rounded-lg font-bold text-[11px] tracking-wide shadow-none border", getStageColor(stage))} variant="outline">
        {label}
      </Badge>
    );
  };

  const getPerformanceStatus = (member: any) => {
    if (member.current_stage === 'COMPLETED') {
      return { label: 'Complete', color: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-lg border border-emerald-100 dark:border-emerald-900/50', icon: CheckCircle };
    }
    if (member.current_stage === 'MANAGER_REVIEW') {
      return { label: 'Review Pending', color: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-lg border border-amber-100 dark:border-amber-900/50', icon: AlertCircle };
    }
    if (member.goals_set === 0) {
      return { label: 'Goals Not Set', color: 'text-rose-600 bg-rose-50 dark:bg-rose-900/20 px-2 py-0.5 rounded-lg border border-rose-100 dark:border-rose-900/50', icon: AlertCircle };
    }
    return { label: 'In Progress', color: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20 px-2 py-0.5 rounded-lg border border-blue-100 dark:border-blue-900/50', icon: Clock };
  };

  // Calculate performance cycle progress (0-100%)
  const calculateCycleProgress = (member: any) => {
    const stages = ['GOAL_SETTING', 'KPI_DEFINITION', 'MANAGER_REVIEW', 'ONE_ON_ONE', 'COMPLETED'];
    const currentStageIndex = stages.indexOf(member.current_stage);
    
    if (currentStageIndex === -1) return 0;
    if (member.current_stage === 'COMPLETED') return 100;
    
    // Base progress on stage completion
    let progress = (currentStageIndex / (stages.length - 1)) * 100;
    
    // Adjust based on goals completion within current stage
    if (member.goals_total > 0) {
      const goalCompletionRate = (member.goals_set / member.goals_total);
      const stageWeight = 100 / (stages.length - 1);
      progress += (goalCompletionRate * stageWeight * 0.5); // Add up to 50% of next stage
    }
    
    return Math.min(Math.round(progress), 95); // Cap at 95% until COMPLETED
  };

  // Calculate summary metrics
  const totalEmployees = teamOverview.length;
  const completedCount = teamOverview.filter((m: any) => m.current_stage === 'COMPLETED').length;
  const reviewPendingCount = teamOverview.filter((m: any) => m.current_stage === 'MANAGER_REVIEW').length;
  const goalsNotSetCount = teamOverview.filter((m: any) => m.goals_set === 0).length;

  // Get lists of employees by status
  const reviewPendingEmployees = teamOverview.filter((m: any) => m.current_stage === 'MANAGER_REVIEW');
  const goalsNotSetEmployees = teamOverview.filter((m: any) => m.goals_set === 0);

  // Pagination for action items
  const paginatedGoalsNotSet = goalsNotSetEmployees.slice(
    (actionItemsPage - 1) * itemsPerPage,
    actionItemsPage * itemsPerPage
  );
  const totalActionItemsPages = Math.ceil(goalsNotSetEmployees.length / itemsPerPage);

  // Pagination for table
  const paginatedTeam = filteredTeam.slice(
    (tablePage - 1) * itemsPerPage,
    tablePage * itemsPerPage
  );
  const totalTablePages = Math.ceil(filteredTeam.length / itemsPerPage);

  const getEmployeeInitials = (name: string) => {
    const parts = name.trim().split(' ');
    const first = parts[0]?.charAt(0) || '';
    const last = parts[parts.length - 1]?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Handler to view employee details in modal
  const handleViewDetails = (employeeId: string) => {
    const employee = employees.find((e: any) => e.id === employeeId);
    const teamMember = teamOverview.find((m: any) => m.employee_id === employeeId);
    setSelectedEmployee({
      ...employee,
      ...teamMember
    });
    setViewGoals(false);
    setIsModalOpen(true);
  };

  // If an employee is selected for full detail view, show their detail view
  if (selectedEmployeeId) {
    return (
      <EmployeeDetailView 
        employeeId={selectedEmployeeId}
        year={year}
        onBack={() => setSelectedEmployeeId(null)}
      />
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-2 font-bold text-slate-400">Loading team data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in fade-in duration-300">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-405 tracking-wide">Total Employees</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{totalEmployees}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Direct Reports</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-blue-50 dark:bg-blue-900/20 text-blue-500 shadow-sm">
                <Users className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-405 tracking-wide">Completed</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{completedCount}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Finished Reviews</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 text-emerald-500 shadow-sm">
                <CheckCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-405 tracking-wide">Review Pending</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{reviewPendingCount}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">Needs Approval</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-amber-50 dark:bg-amber-900/20 text-amber-500 shadow-sm">
                <Clock className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden hover:translate-y-[-2px] transition-all duration-300">
          <CardContent className="p-6">
            <div className="flex justify-between items-start">
              <div className="space-y-4">
                <p className="text-sm font-bold text-slate-405 tracking-wide">Goals Not Set</p>
                <div className="space-y-1">
                  <h3 className="text-4xl font-bold text-slate-800 dark:text-white leading-none">{goalsNotSetCount}</h3>
                  <p className="text-[12px] font-bold text-slate-400 tracking-wide mt-1">No Objectives Set</p>
                </div>
              </div>
              <div className="p-3 rounded-2xl bg-rose-50 dark:bg-rose-900/20 text-rose-500 shadow-sm">
                <AlertCircle className="h-6 w-6" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team Members Table */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Team Performance Status - {year}</CardTitle>
              <p className="text-[11px] text-slate-405 font-bold tracking-widest mt-0.5">Overview of team objectives and progress</p>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search employees..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 rounded-xl border-slate-200 dark:border-slate-800 focus:ring-2 focus:ring-blue-500/20 font-semibold text-sm h-10"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-2 border-slate-50 dark:border-slate-800">
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8">Employee</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Current Stage</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Goals</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Reviews</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Leave Balance</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Cycle Progress</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12">Status</TableHead>
                <TableHead className="text-[13px] font-bold text-slate-400 tracking-wide h-12 px-8 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedTeam.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-20 px-8 text-slate-400 font-bold text-sm">
                    {searchTerm ? 'No employees found matching your search' : 'No team members found'}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedTeam.map((member: any) => {
                  const status = getPerformanceStatus(member);
                  const StatusIcon = status.icon;
                  const cycleProgress = calculateCycleProgress(member);
                  const leaveBalance = calculateLeaveBalance(member.employee_id);
                  
                  return (
                    <TableRow key={member.employee_id} className="group border-b border-slate-50 dark:border-slate-800 last:border-0 hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors cursor-default">
                      <TableCell className="px-8 py-5">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-xl bg-gradient-to-br from-[#143A62]/10 to-blue-500/10 text-[#143A62] dark:text-blue-400 font-bold text-xs flex items-center justify-center border border-slate-100 dark:border-slate-800 shrink-0">
                            {getEmployeeInitials(member.employee_name)}
                          </div>
                          <div>
                            <div className="font-bold text-[#143A62] dark:text-blue-400 text-sm">{member.employee_name}</div>
                            {member.one_on_one_scheduled && (
                              <div className="flex items-center gap-1 text-[10px] font-bold text-emerald-500 mt-0.5">
                                <CheckCircle className="h-3 w-3" />
                                1:1 Scheduled
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-5">{getStageBadge(member.current_stage)}</TableCell>
                      <TableCell className="py-5">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <Target className="h-4 w-4 text-blue-600" />
                            <span className="text-xs font-black text-slate-700 dark:text-slate-200">
                              {member.goals_set} / {member.goals_total}
                            </span>
                          </div>
                          {member.goals_total === 0 ? (
                            <Badge className="px-2 py-0.5 rounded-md font-bold text-[9px] tracking-wider shadow-none border bg-rose-50 text-rose-700 border-rose-200/50 dark:bg-rose-900/20 dark:text-rose-400 dark:border-rose-900/50" variant="outline">
                              No Goals Set
                            </Badge>
                          ) : member.manager_approved ? (
                            <Badge className="px-2 py-0.5 rounded-md font-bold text-[9px] tracking-wider shadow-none border bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/50" variant="outline">
                              Approved
                            </Badge>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell className="py-5">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-purple-600" />
                          <span className="text-xs font-black text-slate-700 dark:text-slate-200">{member.reviews_completed}</span>
                          {member.reviews_completed > 0 && (
                            <Badge className="px-2 py-0.5 rounded-md font-bold text-[9px] tracking-wider shadow-none border bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-900/50" variant="outline">
                              Completed
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-5">
                        <div className="space-y-1 min-w-[120px]">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-blue-600" />
                            <span className="text-xs font-black text-emerald-600">
                              {leaveBalance.available} days
                            </span>
                          </div>
                          <div className="text-[10px] font-bold text-slate-405 tracking-wide">
                            Used: {leaveBalance.used} / {leaveBalance.total}
                            {leaveBalance.pending > 0 && (
                              <span className="text-amber-500 ml-1">
                                ({leaveBalance.pending} pending)
                              </span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="py-5">
                        <div className="space-y-1.5 min-w-[140px] pr-4">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-black text-[#143A62] dark:text-white bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded-md leading-none">{cycleProgress}%</span>
                            <span className="text-[10px] font-bold text-slate-405 tracking-wide">
                              {member.current_stage === 'COMPLETED' ? 'Complete' : 'In Progress'}
                            </span>
                          </div>
                          <Progress 
                            value={cycleProgress} 
                            className="h-1.5 transition-all duration-500 shadow-sm"
                          />
                        </div>
                      </TableCell>
                      <TableCell className="py-5">
                        <span className={cn("text-xs font-bold flex items-center gap-1", status.color)}>
                          <StatusIcon className="h-3.5 w-3.5" />
                          {status.label}
                        </span>
                      </TableCell>
                      <TableCell className="px-8 py-5 text-right">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleViewDetails(member.employee_id)}
                          className="border-slate-200 dark:border-slate-800 hover:bg-[#143A62] hover:text-white dark:hover:bg-blue-600 text-slate-700 dark:text-slate-200 font-bold text-xs px-3 py-1.5 h-8 rounded-lg shadow-sm transition-all"
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          
          {/* Table Pagination */}
          {totalTablePages > 1 && (
            <div className="flex items-center justify-between p-6 border-t border-slate-100 dark:border-slate-800">
              <p className="text-xs font-bold text-slate-405 tracking-wider">
                Showing {((tablePage - 1) * itemsPerPage) + 1} to {Math.min(tablePage * itemsPerPage, filteredTeam.length)} of {filteredTeam.length} employees
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTablePage(p => Math.max(1, p - 1))}
                  disabled={tablePage === 1}
                  className="rounded-lg h-8 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <span className="text-xs font-bold text-slate-405 bg-slate-50 dark:bg-slate-900 border border-slate-150 dark:border-slate-800 px-3 py-1.5 rounded-lg shadow-sm">
                  Page {tablePage} of {totalTablePages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setTablePage(p => Math.min(totalTablePages, p + 1))}
                  disabled={tablePage === totalTablePages}
                  className="rounded-lg h-8 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Items */}
      <Card className="border border-slate-100 dark:border-slate-800 shadow-md bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
        <CardHeader className="pb-5 pt-6 px-8 border-b border-slate-50 dark:border-slate-800/50">
          <CardTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 tracking-tight">Action Items</CardTitle>
        </CardHeader>
        <CardContent className="p-8">
          <div className="space-y-4">
            {reviewPendingCount > 0 && (
              <div className="p-5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-100 dark:border-amber-900/50 rounded-2xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-xl shrink-0 mt-0.5">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#143A62] dark:text-amber-400 text-sm tracking-tight mb-2">
                      Employees waiting for manager review:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {reviewPendingEmployees.map((emp: any) => (
                        <div key={emp.employee_id} className="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-xl border border-amber-200/40 dark:border-amber-900/40 shadow-sm hover:translate-y-[-1px] transition-all">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            {emp.employee_name}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewDetails(emp.employee_id)}
                            className="h-7 text-xs font-bold text-amber-700 hover:text-amber-800 bg-amber-50 hover:bg-amber-100 dark:bg-amber-950/30 dark:hover:bg-amber-900/40 rounded-lg"
                          >
                            Review
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {goalsNotSetCount > 0 && (
              <div className="p-5 bg-rose-50/50 dark:bg-rose-900/10 border border-rose-100 dark:border-rose-900/50 rounded-2xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="p-2 bg-rose-100 dark:bg-rose-900/30 text-rose-600 rounded-xl shrink-0 mt-0.5">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <p className="font-bold text-[#143A62] dark:text-rose-400 text-sm tracking-tight mb-2">
                      Employees who haven't started their {year} goals:
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                      {paginatedGoalsNotSet.map((emp: any) => (
                        <div key={emp.employee_id} className="flex items-center justify-between bg-white dark:bg-slate-900 p-3 rounded-xl border border-rose-200/40 dark:border-rose-900/40 shadow-sm hover:translate-y-[-1px] transition-all">
                          <span className="text-xs font-bold text-slate-700 dark:text-slate-200">
                            {emp.employee_name}
                          </span>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleViewDetails(emp.employee_id)}
                            className="h-7 text-xs font-bold text-rose-700 hover:text-rose-800 bg-rose-50 hover:bg-rose-100 dark:bg-rose-900/30 dark:hover:bg-rose-900/40 rounded-lg"
                          >
                            View
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* Action Items Pagination */}
                    {totalActionItemsPages > 1 && (
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-rose-100 dark:border-rose-900/40">
                        <p className="text-[10px] font-bold text-rose-700 dark:text-rose-400">
                          Showing {((actionItemsPage - 1) * itemsPerPage) + 1} to {Math.min(actionItemsPage * itemsPerPage, goalsNotSetEmployees.length)} of {goalsNotSetEmployees.length}
                        </p>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setActionItemsPage(p => Math.max(1, p - 1))}
                            disabled={actionItemsPage === 1}
                            className="h-7 w-7 p-0 rounded-lg"
                          >
                            <ChevronLeft className="h-4 w-4" />
                          </Button>
                          <span className="text-xs font-bold text-rose-700 dark:text-rose-400">
                            {actionItemsPage} / {totalActionItemsPages}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setActionItemsPage(p => Math.min(totalActionItemsPages, p + 1))}
                            disabled={actionItemsPage === totalActionItemsPages}
                            className="h-7 w-7 p-0 rounded-lg"
                          >
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            
            {completedCount === totalEmployees && totalEmployees > 0 && (
              <div className="flex items-center gap-4 p-5 bg-emerald-50/50 dark:bg-emerald-900/10 border border-emerald-100 dark:border-emerald-900/50 rounded-2xl shadow-sm animate-pulse">
                <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-xl shrink-0">
                  <CheckCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-bold text-[#143A62] dark:text-emerald-400 text-sm tracking-tight">
                    All employees have completed their performance cycle!
                  </p>
                  <p className="text-xs font-semibold text-slate-500 mt-0.5">Great work managing employee performance reviews.</p>
                </div>
              </div>
            )}
            
            {totalEmployees === 0 && (
              <div className="flex items-center justify-center p-12 text-slate-400">
                <div className="text-center">
                  <Users className="h-12 w-12 mx-auto mb-4 opacity-30 text-slate-400" />
                  <p className="font-bold">No team members assigned</p>
                  <p className="text-xs font-semibold mt-1">Employees reporting to you will appear here.</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Employee Details Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="w-[min(96vw,800px)] max-w-[800px] max-h-[92vh] flex flex-col rounded-2xl bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-0 transition-colors duration-300 gap-0 overflow-hidden shadow-2xl">
          <DialogHeader className="p-6 pb-4 border-b border-slate-100 dark:border-slate-800 shrink-0">
            <DialogTitle className="text-xl font-bold text-[#143A62] dark:text-slate-100 flex items-center gap-2">
              <Users className="h-5 w-5 text-blue-500" />
              Employee Performance Profile
            </DialogTitle>
            <DialogDescription className="text-xs font-semibold text-slate-400 mt-1 tracking-wider">
              View employee information and performance status
            </DialogDescription>
          </DialogHeader>
          
          {selectedEmployee && (
            <div className="p-6 space-y-6 overflow-y-auto flex-1 custom-scrollbar">
              {/* Basic Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-4 p-4 border border-slate-100 dark:border-slate-800/80 rounded-2xl bg-slate-50/20 dark:bg-slate-900/30">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 tracking-widest">Full Name</label>
                    <p className="text-sm font-black text-[#143A62] dark:text-white mt-1">
                      {selectedEmployee.employee_name || `${selectedEmployee.first_name || ''} ${selectedEmployee.last_name || ''}`}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                      <Mail className="h-3.5 w-3.5" />
                      Email Address
                    </label>
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mt-1">{selectedEmployee.email || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5" />
                      Phone Number
                    </label>
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mt-1">{selectedEmployee.phone || selectedEmployee.phone_number || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="space-y-4 p-4 border border-slate-100 dark:border-slate-800/80 rounded-2xl bg-slate-50/20 dark:bg-slate-900/30">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5" />
                      Department
                    </label>
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mt-1">{selectedEmployee.department || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-black text-slate-400 tracking-widest flex items-center gap-2">
                      <Briefcase className="h-3.5 w-3.5" />
                      Job Title
                    </label>
                    <p className="text-xs font-bold text-slate-600 dark:text-slate-300 mt-1">{selectedEmployee.job_title || 'N/A'}</p>
                  </div>
                  
                  <div>
                    <label className="text-[10px] font-black text-slate-400 tracking-widest">Employee ID</label>
                    <p className="text-[11px] mt-1 font-mono font-bold text-slate-400">{selectedEmployee.employee_id || selectedEmployee.id}</p>
                  </div>
                </div>
              </div>

              {/* Performance Status */}
              <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                <h4 className="text-xs font-black text-slate-400 tracking-widest mb-4">Performance Status ({year})</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-4 bg-blue-50/40 dark:bg-blue-900/20 rounded-2xl border border-blue-100/50 dark:border-blue-900/30">
                    <label className="text-[9px] font-black text-blue-500 tracking-widest">Current Stage</label>
                    <div className="mt-2">
                      {getStageBadge(selectedEmployee.current_stage)}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-purple-50/40 dark:bg-purple-900/20 rounded-2xl border border-purple-100/50 dark:border-purple-900/30">
                    <label className="text-[9px] font-black text-purple-500 tracking-widest">Goals Progress</label>
                    <p className="text-lg font-black mt-1 text-purple-900 dark:text-purple-100 leading-none">
                      {selectedEmployee.goals_set} / {selectedEmployee.goals_total}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-emerald-50/40 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100/50 dark:border-emerald-900/30">
                    <label className="text-[9px] font-black text-emerald-500 tracking-widest">Reviews Completed</label>
                    <p className="text-lg font-black mt-1 text-emerald-900 dark:text-emerald-100 leading-none">
                      {selectedEmployee.reviews_completed || 0}
                    </p>
                  </div>
                  
                  <div className="p-4 bg-orange-50/40 dark:bg-orange-900/20 rounded-2xl border border-orange-100/50 dark:border-orange-900/30">
                    <label className="text-[9px] font-black text-orange-500 tracking-widest">Cycle Progress</label>
                    <div className="mt-2 space-y-1.5">
                      <Progress value={calculateCycleProgress(selectedEmployee)} className="h-1.5 shadow-sm" />
                      <p className="text-[10px] font-bold text-slate-400 mt-1">
                        {calculateCycleProgress(selectedEmployee)}% Complete
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Goals Section */}
              {!viewGoals ? (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                  <Button 
                    onClick={() => setViewGoals(true)} 
                    className="w-full bg-[#143A62] hover:bg-[#1e3a5f] dark:bg-blue-600 dark:hover:bg-blue-700 text-white font-bold h-11 rounded-xl shadow-md transition-all gap-2 text-xs"
                  >
                    <Target className="h-4 w-4" />
                    View & Review Goals ({selectedEmployee.goals_total})
                  </Button>
                </div>
              ) : (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-[10px] font-black text-slate-400 tracking-widest">Employee Goals for {year}</h4>
                    <Button 
                      onClick={() => setViewGoals(false)} 
                      variant="ghost"
                      size="sm"
                      className="text-xs font-bold text-[#143A62] hover:text-[#1e3a5f] bg-slate-50 dark:bg-slate-800 dark:text-slate-200 rounded-lg"
                    >
                      Back to Summary
                    </Button>
                  </div>

                  {employeeGoals.length === 0 ? (
                    <div className="text-center py-12 border border-dashed rounded-2xl bg-slate-50/20 text-slate-400">
                      <Target className="h-12 w-12 mx-auto mb-3 opacity-20" />
                      <p className="text-xs font-bold">No goals set yet for {year}</p>
                    </div>
                  ) : (
                    <div className="space-y-4 max-h-[400px] overflow-y-auto pr-1 custom-scrollbar">
                      {employeeGoals.map((goal: any) => (
                        <Card key={goal.id} className="border border-slate-100 dark:border-slate-800 shadow-sm bg-white dark:bg-slate-900 overflow-hidden rounded-2xl">
                          <CardContent className="p-5">
                            <div className="space-y-4">
                              <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                  <h5 className="font-bold text-[#143A62] dark:text-blue-400 text-sm leading-snug">{goal.title}</h5>
                                  {goal.description && <p className="text-xs text-slate-400 dark:text-slate-505 mt-1 italic leading-normal">{goal.description}</p>}
                                </div>
                                <Badge 
                                  className={cn("px-2 py-0.5 rounded-lg font-bold text-[9px] tracking-wide shadow-none border shrink-0",
                                    goal.manager_approved 
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200/50 dark:bg-emerald-950/20 dark:text-emerald-400 dark:border-emerald-900/50' 
                                      : 'bg-amber-50 text-amber-700 border-amber-200/50 dark:bg-amber-900/20 dark:text-amber-400 dark:border-emerald-900/50'
                                  )}
                                  variant="outline"
                                >
                                  {goal.manager_approved ? 'Approved' : 'Pending Review'}
                                </Badge>
                              </div>

                              <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50/50 dark:bg-slate-900/50 rounded-xl border border-slate-100/50 dark:border-slate-850/50 text-[10px] font-bold text-slate-400 tracking-widest">
                                <div>
                                  <span>Category</span>
                                  <p className="font-bold text-slate-700 dark:text-slate-200 mt-1">{goal.category || 'N/A'}</p>
                                </div>
                                <div>
                                  <span>Priority</span>
                                  <p className="font-bold text-slate-700 dark:text-slate-200 mt-1">{goal.priority || 'N/A'}</p>
                                </div>
                                <div>
                                  <span>Weight</span>
                                  <p className="font-bold text-slate-700 dark:text-slate-200 mt-1">{goal.weight || 0}%</p>
                                </div>
                              </div>

                              {goal.kpis && goal.kpis.length > 0 && (
                                <div className="border-t border-slate-100 dark:border-slate-800 pt-3">
                                  <p className="text-[10px] font-black text-slate-400 tracking-widest mb-2">Key Performance Indicators</p>
                                  <div className="space-y-1.5">
                                    {goal.kpis.map((kpi: any, idx: number) => (
                                      <div key={idx} className="text-xs font-semibold text-slate-600 dark:text-slate-300 flex items-center gap-2">
                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0"></span>
                                        <span>{kpi.metric}: <span className="font-bold text-slate-800 dark:text-slate-200 bg-slate-50 dark:bg-slate-800 px-1.5 py-0.5 rounded">{kpi.target} {kpi.unit}</span></span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {goal.manager_comments && (
                                <div className="bg-blue-50/50 dark:bg-blue-900/20 p-3 rounded-xl border border-blue-100/50 dark:border-blue-900/30">
                                  <p className="text-[9px] font-black text-blue-500 tracking-widest">Manager Comments</p>
                                  <p className="text-xs font-semibold text-blue-900 dark:text-blue-200 mt-1">{goal.manager_comments}</p>
                                </div>
                              )}

                              {!goal.manager_approved && (
                                <div className="flex gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
                                  <Button
                                    size="sm"
                                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-9 rounded-xl shadow-sm transition-all gap-1.5 text-xs"
                                    onClick={() => approveGoalMutation.mutate({ goalId: goal.id })}
                                    disabled={approveGoalMutation.isPending}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                    Approve
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="flex-1 border-rose-200 text-rose-600 hover:bg-rose-50 dark:border-rose-900/30 dark:text-rose-400 dark:hover:bg-rose-900/30 font-bold h-9 rounded-xl shadow-sm transition-all gap-1.5 text-xs"
                                    onClick={() => {
                                      const comments = prompt('Enter feedback for rejection:');
                                      if (comments) {
                                        rejectGoalMutation.mutate({ goalId: goal.id, comments });
                                      }
                                    }}
                                    disabled={rejectGoalMutation.isPending}
                                  >
                                    <AlertCircle className="h-4 w-4" />
                                    Reject
                                  </Button>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter className="p-6 pt-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/10 shrink-0">
            <Button onClick={() => setIsModalOpen(false)} className="rounded-xl font-bold h-11 border-slate-200 dark:border-slate-800 px-6">
              Close Profile
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ManagerTeamView;